import { CellStyle, ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateMachineTypeMutation } from '../../../../hooks/mutations/useMachineTypeMutation';
import { useMachinesTypesQuery } from '../../../../hooks/queries/useMachinesTypesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { MachineType } from '../../../../services/apiSLTP/materials/machinesTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getStatusColor } from '../../../../utils/colors';
import { crudStatus, getCrudStatusInfos, getPriceTypeInfos, localStorageKeys, permissionLevels, systemConfigKeyEnum } from '../../../../utils/enums';
import { formatNumber } from '../../../../utils/numbers';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import IconLine from '../../../IconLine';
import UpdateMachineTypeModal from '../../../modals/admin/material/UpdateMachineTypeModal';
import DeleteModal from '../../../modals/DeleteModal';
import GenericAgGridTable from '../../GenericAgGridTable';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import IconButton from '../../../shared/IconButton';
import { useTheme } from '../../../../hooks/useTheme';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

interface MachinesTypesTableProps {
    withUsageCount: boolean;
}

const MachinesTypesTable: React.FC<MachinesTypesTableProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedMachineType, setSelectedMachineType] = useState<MachineType | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { muiTheme } = useTheme();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const { data, isLoading, refetch } = useMachinesTypesQuery({
        queries: { withUsage: withUsageCount },
    });

    const { mutate: mutateMachineType } = useUpdateMachineTypeMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating machineType:', error.message),
    });

    const columnDefs: ColDef<MachineType>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),

            cellRenderer: (params: ICellRendererParams<MachineType>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyMachineType'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedMachineType(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteMachineType'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedMachineType(params.data);
                                    setIsEditMode(false);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatus.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<MachineType>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: `${capitalizeFirstLetter(t('price'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            valueFormatter: (params) => formatNumber(params.value, t('locale')),
            field: 'price',
            cellStyle: (params) => {
                if (!params.data) return null;
                return params.data?.price === 0 ? { color: muiTheme.palette.error.main } : undefined;
            },
            colId: 'price',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeInfos(params.data?.priceType))),
            colId: 'pricing',
        },
        {
            headerName: capitalizeFirstLetter(t('displayOrder')),
            field: 'displayOrder',
            colId: 'displayOrder',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        createDateTimeColumn<MachineType>('createdAt', 'createdAt'),
        createDateTimeColumn<MachineType>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevels.ADMIN)
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                initialWidth: 250,
                colId: '_id',
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                colId: 'xtotal',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xMachines')),
                field: 'usageCount.machines',
                colId: 'xmachines',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            }
        );

    const sortedData = useMemo(() => {
        return data?.slice().sort((a, b) => a.designation.localeCompare(b.designation)) ?? [];
    }, [data]);

    return (
        <>
            <GenericAgGridTable
                rowData={sortedData}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName="admin-machineTypes"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedMachineType(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addMachineType'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.MATERIAL.MACHINETYPES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateMachineTypeModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    machineType={selectedMachineType}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}

            {showDeleteModal && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        if (selectedMachineType) {
                            mutateMachineType({
                                machineTypeId: selectedMachineType._id,
                                data: { crudStatus: crudStatus.DELETED },
                            });
                        }
                    }}
                    title={t('machine')}
                    itemName={selectedMachineType?.designation}
                />
            )}
        </>
    );
};

export default MachinesTypesTable;
